import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { isEqual } from 'lodash';
import { Grid, Typography, Link, IconButton, Box, Chip, Breadcrumbs, Stack, TextField, Alert, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useBeforeUnload, useNavigate, useParams, useLocation } from 'react-router-dom';
import { statusChips } from 'data/OrderItemStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong } from '@fortawesome/free-solid-svg-icons';
import { OrderItem } from 'models/OrderItemModel';
import Data from 'models/DataModel';
import { Comment } from 'models/CommentModel';
import { ProductType } from 'data/ProductType';
import orderService from 'api/orderService';
import useAuth from 'hooks/useAuth';
import { useOrderItemDraft } from 'hooks/useOrderItemDraft';
import { useSerializedSaveQueue } from 'hooks/useSerializedSaveQueue';
import type { SerializedSaveContext } from 'hooks/useSerializedSaveQueue';
import { extractFilenameFromAttachmentUrl, fileToBase64, isValidFileType } from 'utils/fileUtilities';
import { getLockOwnerName } from 'utils/orderLocking';

import MainCard from 'components/MainCard';
import Questions, { UploadedFile, QuestionsHandle } from 'components/Questions';
import Comments from 'components/Comments';
import UpdateOrderItemStatus from 'components/UpdateOrderItemStatus';
import OrderAttachments, { AttachmentReplacementStatus } from 'components/OrderAttachments';
import LockedOrder from 'components/LockedOrder';
import { AttachmentType } from 'data/AttachmentType';
import { OrderItemViewType } from 'types/orderitemviewtype';
import { EditOrderContext } from 'contexts/EditOrderContext';
import { STATUS_CANCELLED, STATUS_COMPLETE } from '../../constants';
import { deleteAttachmentFromData, replaceQuestionAttachmentInData } from 'helpers/orderItemAttachmentsHelper';
import { DataMergeConflict, mergeOrderItemData, OrderItemDataMergeConflictError } from 'utils/orderItemDataMerge';

import {
  useComponentTracking,
  useActionTracking,
  usePageViewTracking,
  useErrorTracking,
  usePerformanceTracking
} from '../../hooks/useTelemtry';

const DEFAULT_EDIT_LEASE_HEARTBEAT_INTERVAL_MINUTES = 5;
const configuredEditLeaseHeartbeatIntervalMinutes = Number(import.meta.env.VITE_APP_ORDER_ITEM_EDIT_LEASE_HEARTBEAT_INTERVAL_MINUTES);
const EDIT_LEASE_HEARTBEAT_INTERVAL_MS =
  (Number.isFinite(configuredEditLeaseHeartbeatIntervalMinutes) && configuredEditLeaseHeartbeatIntervalMinutes > 0
    ? configuredEditLeaseHeartbeatIntervalMinutes
    : DEFAULT_EDIT_LEASE_HEARTBEAT_INTERVAL_MINUTES) *
  60 *
  1000;

const formatDraftTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return 'an earlier session';
  }

  return date.toLocaleString();
};

const areDataEqual = (left: Data, right: Data): boolean => isEqual(left, right);

const getErrorStatus = (error: unknown): number | undefined => {
  const errorWithStatus = error as { status?: number } | null | undefined;

  return errorWithStatus?.status;
};

const isConflictError = (error: unknown): boolean => getErrorStatus(error) === 412;

const getConflictLabel = (conflict: DataMergeConflict): string => {
  switch (conflict.kind) {
    case 'answer':
      return `answer ${conflict.questionId}`;
    case 'token':
      return `token ${conflict.questionId}.${conflict.token}`;
    case 'address':
      return 'address';
    case 'attachments':
      return 'attachments';
    case 'data':
      return conflict.field;
    default:
      return 'field';
  }
};

const formatConflictSummary = (conflicts: DataMergeConflict[]): string => {
  const labels = Array.from(new Set(conflicts.map(getConflictLabel)));
  const shownLabels = labels.slice(0, 3).join(', ');

  if (labels.length <= 3) {
    return shownLabels;
  }

  return `${shownLabels}, and ${labels.length - 3} more`;
};

interface DraftSaveUpdates {
  data: string;
  status?: string;
}

interface ResolveConflictAndRetryOptions {
  originalError: unknown;
  updates: DraftSaveUpdates;
  saveData: Data;
  saveContext: SerializedSaveContext;
  shouldPromoteStatus: boolean;
}

interface EditOrderLocationState {
  fromTab?: OrderItemViewType;
  returnPath?: string;
  editLeaseId?: string | null;
}

const QuestionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const { user } = useAuth();
  const navState = location.state as EditOrderLocationState | undefined;

  const [order, setOrder] = useState<OrderItem>();
  const [customerUploadedPlans, setCustomerUploadedPlans] = useState<URL[]>([]);
  const [canReplaceCustomerUploadedPlans, setCanReplaceCustomerUploadedPlans] = useState(false);
  const [lockOwnerName, setLockOwnerName] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [questionsLoadError, setQuestionsLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [questionSetVersion, setQuestionSetVersion] = useState<string | null>(null);
  const draftIdentity = useMemo(
    () =>
      user?.userId && orderId && questionSetVersion
        ? {
            userId: user.userId,
            orderItemId: orderId,
            questionSetVersion
          }
        : null,
    [orderId, questionSetVersion, user?.userId]
  );
  const {
    snapshot: draftSnapshot,
    answers: draftAnswers,
    tokens: draftTokens,
    address: draftAddress,
    attachments: draftAttachments,
    conflicts: draftConflicts,
    pendingDraft,
    lastPersistedSnapshot,
    applyServerSnapshot,
    markSnapshotPersisted,
    setMergeConflicts,
    restorePendingDraft,
    discardPendingDraft,
    setAnswerValue,
    setTokenValue,
    setAddressValue,
    setAttachments
  } = useOrderItemDraft(draftIdentity);

  const etagRef = useRef<string | null>(null);
  const [editLeaseId, setEditLeaseId] = useState<string | null>(navState?.editLeaseId ?? null);
  const editLeaseIdRef = useRef<string | null>(navState?.editLeaseId ?? null);
  const editLeaseHeartbeatInFlightRef = useRef(false);
  const [reportRegenerationStatus, setReportRegenerationStatus] = useState<'idle' | 'loading' | 'requested' | 'failed'>('idle');
  const [customerUploadedPlansReplacementStatus, setCustomerUploadedPlansReplacementStatus] =
    useState<AttachmentReplacementStatus>('idle');

  const questionsRef = useRef<QuestionsHandle>(null);
  const shouldSetStatusToInProgress = useRef<boolean>(false);

  const status = order && statusChips[order.status];
  const comments = order?.comments;
  const isHistory = status?.orderItemViewType === OrderItemViewType.Historic;
  const isEditDisabled = isHistory || !editLeaseId;

  // ==================== Telemetry Hooks ====================

  useComponentTracking('QuestionsPage', {
    orderId,
    orderStatus: order?.status,
    productType: order?.productType,
    isHistory
  });

  const { trackClick, trackCustomAction, trackFileUpload, trackFeature } = useActionTracking();

  usePageViewTracking('Order Questions', {
    orderId,
    orderStatus: order?.status,
    productType: order?.productType,
    hasComments: (order?.comments?.length || 0) > 0
  });

  const trackError = useErrorTracking('QuestionsPage');
  const { startMeasure, endMeasure } = usePerformanceTracking();

  const setCurrentEditLeaseId = useCallback((leaseId: string | null) => {
    editLeaseIdRef.current = leaseId;
    setEditLeaseId(leaseId);
  }, []);

  const handleEditLeaseConflict = useCallback(
    (leaseOwnerName: string | null, operation: string): boolean => {
      if (!leaseOwnerName) return false;

      setCurrentEditLeaseId(null);
      setLockOwnerName(leaseOwnerName);
      trackCustomAction('OrderLockConflict', {
        orderId,
        lockOwner: leaseOwnerName,
        operation
      });

      return true;
    },
    [orderId, setCurrentEditLeaseId, trackCustomAction]
  );

  const throwIfEditLeaseConflict = useCallback(
    (leaseOwnerName: string | null, operation: string): void => {
      if (handleEditLeaseConflict(leaseOwnerName, operation)) {
        throw new Error(`Order item edit lease is held by ${leaseOwnerName}.`);
      }
    },
    [handleEditLeaseConflict]
  );

  const handleStatusEditLeaseConflict = useCallback(
    (leaseOwnerName: string): void => {
      handleEditLeaseConflict(leaseOwnerName, 'updateStatus');
    },
    [handleEditLeaseConflict]
  );

  const handleCommentEditLeaseConflict = useCallback(
    (leaseOwnerName: string): void => {
      handleEditLeaseConflict(leaseOwnerName, 'addComment');
    },
    [handleEditLeaseConflict]
  );

  // ==================== Data Loading ====================

  const loadCustomerUploadedPlans = useCallback(
    async (orderId: string) => {
      try {
        const attachment = await orderService.getCustomerUploadedPlans(orderId);
        return attachment;
      } catch (error) {
        console.warn('Failed to load customer uploaded plans:', error);
        trackError(error as Error, {
          operation: 'loadCustomerUploadedPlans'
        });
        return {
          attachmentUrls: [],
          canReplace: false,
          isOverride: false
        };
      }
    },
    [trackError]
  );

  const acquireEditLease = useCallback(
    async (): Promise<void> => {
      if (!orderId) return;

      try {
        const lease = await orderService.acquireOrderItemEditLease(orderId, editLeaseIdRef.current);
        setCurrentEditLeaseId(lease.id);
      } catch (error: any) {
        if (handleEditLeaseConflict(getLockOwnerName(error), 'acquireEditLease')) {
          return;
        }

        console.error('Failed to acquire order item edit lease:', error);
        trackError(error as Error, {
          operation: 'acquireEditLease'
        });
      }
    },
    [handleEditLeaseConflict, orderId, setCurrentEditLeaseId, trackError]
  );

  const releaseEditLease = useCallback(
    async (): Promise<void> => {
      if (!orderId || !editLeaseIdRef.current) return;

      const leaseId = editLeaseIdRef.current;

      try {
        await orderService.releaseOrderItemEditLease(orderId, leaseId);
        setCurrentEditLeaseId(null);
      } catch (error) {
        console.error('Failed to release order item edit lease:', error);
        trackError(error as Error, {
          operation: 'releaseEditLease'
        });
      }
    },
    [orderId, setCurrentEditLeaseId, trackError]
  );

  useEffect(() => {
    setReportRegenerationStatus('idle');
    setCustomerUploadedPlansReplacementStatus('idle');
  }, [orderId]);

  // Refetch order only when the latest server snapshot is needed.
  const refetchOrder = useCallback(
    async (options?: {
      shouldApply?: () => boolean;
      reason?: string;
    }): Promise<{ orderItem: OrderItem; etag: string | null } | undefined> => {
      if (!orderId) return;

      try {
        const { orderItem, etag: responseETag } = await orderService.getOrderItem(orderId);
        etagRef.current = responseETag;

        if (options?.shouldApply?.() ?? true) {
          setOrder(orderItem);
          applyServerSnapshot(orderItem.data, responseETag);
        }

        trackCustomAction('OrderRefetched', {
          orderId,
          reason: options?.reason ?? 'attachmentConcurrency'
        });

        return { orderItem, etag: responseETag };
      } catch (error) {
        console.error('Failed to refetch order:', error);
        trackError(error as Error, {
          operation: 'refetchOrder'
        });
        throw error;
      }
    },
    [applyServerSnapshot, orderId, trackCustomAction, trackError]
  );

  // Load order data and attachments
  useEffect(() => {
    if (!orderId) return;

    let isActive = true;

    const loadOrderData = async () => {
      startMeasure('LoadOrderData');

      try {
        const { orderItem, etag: responseETag } = await orderService.getOrderItem(orderId);
        setOrder(orderItem);
        etagRef.current = responseETag;
        applyServerSnapshot(orderItem.data, responseETag);
        shouldSetStatusToInProgress.current = ['New', 'On hold'].includes(orderItem.status);

        const customerUploadedPlans = await loadCustomerUploadedPlans(orderId);
        setCustomerUploadedPlans(customerUploadedPlans.attachmentUrls);
        setCanReplaceCustomerUploadedPlans(customerUploadedPlans.canReplace);

        endMeasure('LoadOrderData', {
          orderId,
          orderStatus: orderItem.status,
          productType: orderItem.productType,
          attachmentCount: customerUploadedPlans.attachmentUrls.length
        });

        trackCustomAction('OrderLoaded', {
          orderId,
          orderStatus: orderItem.status,
          productType: orderItem.productType,
          hasAttachments: customerUploadedPlans.attachmentUrls.length > 0
        });
      } catch (error) {
        console.error('Failed to load order:', error);
        trackError(error as Error, {
          operation: 'loadOrderData'
        });
        endMeasure('LoadOrderData', {
          error: true
        });
      }
    };

    const loadEditPage = async () => {
      setIsLoading(true);

      // Keep the edit surface out of reach until lease acquisition has settled.
      await Promise.allSettled([acquireEditLease(), loadOrderData()]);

      if (isActive) {
        setIsLoading(false);
      }
    };

    loadEditPage();

    return () => {
      isActive = false;
      releaseEditLease();
    };
  }, [
    acquireEditLease,
    applyServerSnapshot,
    orderId,
    releaseEditLease,
    startMeasure,
    endMeasure,
    trackError,
    trackCustomAction,
    loadCustomerUploadedPlans
  ]);

  // Tab/window close cannot wait for async release; this is best-effort and lease expiry protects abandoned sessions.
  useBeforeUnload(() => releaseEditLease());

  useEffect(() => {
    if (!orderId || !editLeaseId) return;

    let isActive = true;

    const renewEditLease = async () => {
      const leaseId = editLeaseIdRef.current;

      if (!leaseId || editLeaseHeartbeatInFlightRef.current) return;

      editLeaseHeartbeatInFlightRef.current = true;

      try {
        const lease = await orderService.renewOrderItemEditLease(orderId, leaseId);

        if (isActive && editLeaseIdRef.current === leaseId) {
          setCurrentEditLeaseId(lease.id);
        }
      } catch (error) {
        if (!isActive) return;

        if (handleEditLeaseConflict(getLockOwnerName(error), 'renewEditLease')) {
          return;
        }

        console.error('Failed to renew order item edit lease:', error);
        trackError(error as Error, {
          operation: 'renewEditLease'
        });
      } finally {
        editLeaseHeartbeatInFlightRef.current = false;
      }
    };

    const intervalId = window.setInterval(renewEditLease, EDIT_LEASE_HEARTBEAT_INTERVAL_MS);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [editLeaseId, handleEditLeaseConflict, orderId, setCurrentEditLeaseId, trackError]);

  // ==================== Mutate + Persist Helper ====================

  type DataUpdater = (current: Data) => Data;

  const mutateOrderData = useCallback(
    async (updater: DataUpdater, onConflict?: (currentOrder: OrderItem | undefined) => Promise<void> | void): Promise<void> => {
      if (!orderId || !editLeaseIdRef.current) return;

      const baseData = draftSnapshot;
      if (!baseData) return;

      const nextData = updater(baseData);

      // If updater makes no changes (e.g. attachment already present), skip persistence to avoid unnecessary ETag conflicts.
      if (nextData === baseData) return;

      setOrder((prev) => {
        if (!prev) return prev;
        return { ...prev, data: nextData };
      });
      setAttachments(nextData.Attachments ?? []);

      try {
        const saveETag = etagRef.current;
        const updateResult = await orderService.updateOrderItem(
          orderId,
          { data: JSON.stringify(nextData) },
          saveETag,
          editLeaseIdRef.current
        );
        throwIfEditLeaseConflict(updateResult.lockOwnerName, 'mutateOrderData');

        if (updateResult.etag) {
          etagRef.current = updateResult.etag;
          markSnapshotPersisted(nextData, updateResult.etag);
          return;
        }

        const refetchedOrder = await refetchOrder({ reason: 'missingSaveETag' });
        markSnapshotPersisted(refetchedOrder?.orderItem.data ?? nextData, refetchedOrder?.etag ?? etagRef.current);
      } catch (err: any) {
        if (isConflictError(err)) {
          const currentOrder = await refetchOrder();

          if (onConflict) {
            await onConflict(currentOrder?.orderItem);
          }

          return;
        }

        trackError(err as Error, {
          operation: 'mutateOrderData',
          orderId
        });

        throw err;
      }
    },
    [draftSnapshot, markSnapshotPersisted, orderId, refetchOrder, setAttachments, throwIfEditLeaseConflict, trackError]
  );

  // ==================== Event Handlers ====================

  const addComment = useCallback(
    (text: string) => {
      const newComment: Comment = {
        userName: user?.name!,
        timestamp: new Date(),
        text: text
      };
      setOrder((prev) => ({ ...prev!, comments: [newComment, ...prev!.comments] }));

      trackCustomAction('CommentAdded', {
        orderId,
        commentLength: text.length
      });
    },
    [user?.name, orderId, trackCustomAction]
  );

  const handleSingleFileUpload = useCallback(
    async (questionId: string, file: File): Promise<void> => {
      if (!orderId || isHistory) return;
      if (!editLeaseIdRef.current) {
        throw new Error('Cannot upload file without an edit lease.');
      }

      startMeasure('FileUpload');

      try {
        const existingAttachments = order?.data.Attachments?.filter((a) => a && a.includes(`${questionId}.`)) ?? [];

        for (const attachmentUrl of existingAttachments) {
          const url = new URL(attachmentUrl);
          const filename = extractFilenameFromAttachmentUrl(url);
          await orderService.deleteAttachment(orderId, filename, AttachmentType.Plans, editLeaseIdRef.current);
        }

        const byteArray = await fileToBase64(file);
        const attachmentUri = await orderService.postAttachments(
          orderId,
          questionId,
          byteArray,
          AttachmentType.Plans,
          file.type,
          editLeaseIdRef.current
        );

        await mutateOrderData(replaceQuestionAttachmentInData(questionId, attachmentUri), async (currentOrder) => {
          const ourAttachmentExists = currentOrder?.data.Attachments?.includes(attachmentUri);

          trackCustomAction('ConcurrencyConflictDetected', {
            orderId,
            operation: 'fileUpload',
            questionId,
            attachmentAlreadyPresent: ourAttachmentExists
          });

          if (!ourAttachmentExists) {
            alert(
              'Another attachment was uploaded at the same time. ' +
                'Your file has been uploaded but may need to be re-added. ' +
                'Please check the attachments and upload again if needed.'
            );
          }
        });

        endMeasure('FileUpload', {
          orderId,
          questionId,
          fileSize: file.size,
          fileType: file.type,
          success: true
        });

        trackFileUpload(file.name, file.size, file.type, {
          orderId,
          questionId
        });
      } catch (error) {
        const leaseOwnerName = getLockOwnerName(error);

        if (handleEditLeaseConflict(leaseOwnerName, 'handleSingleFileUpload')) {
          throw error;
        }

        console.error(`Failed to upload file for question ${questionId}:`, error);

        trackError(error as Error, {
          operation: 'handleSingleFileUpload',
          questionId,
          fileName: file.name
        });

        endMeasure('FileUpload', {
          orderId,
          questionId,
          error: true
        });

        throw error;
      }
    },
    [
      orderId,
      isHistory,
      order?.data.Attachments,
      startMeasure,
      endMeasure,
      trackFileUpload,
      trackError,
      trackCustomAction,
      mutateOrderData,
      handleEditLeaseConflict
    ]
  );

  const handleFileDelete = useCallback(
    async (fileUrl: URL): Promise<void> => {
      if (!orderId || isHistory || !editLeaseIdRef.current) return;

      try {
        const filename = extractFilenameFromAttachmentUrl(fileUrl);
        await orderService.deleteAttachment(orderId, filename, AttachmentType.Plans, editLeaseIdRef.current);

        await mutateOrderData(deleteAttachmentFromData(fileUrl.toString()));

        trackCustomAction('FileDeleted', {
          orderId,
          fileName: filename
        });
      } catch (error) {
        if (handleEditLeaseConflict(getLockOwnerName(error), 'handleFileDelete')) {
          return;
        }

        console.error('Error deleting file:', error);
        trackError(error as Error, {
          operation: 'handleFileDelete',
          fileUrl: fileUrl.toString()
        });
      }
    },
    [orderId, isHistory, mutateOrderData, trackCustomAction, trackError, handleEditLeaseConflict]
  );

  const updateStatus = useCallback(
    (newStatus: string, comment?: string | null) => {
      setOrder((prev) => ({ ...prev!, status: newStatus }));
      if (comment) {
        addComment(comment);
      }

      trackCustomAction('OrderStatusUpdated', {
        orderId,
        oldStatus: order?.status,
        newStatus,
        hasComment: !!comment
      });
    },
    [addComment, orderId, order?.status, trackCustomAction]
  );

  const resolveConflictAndRetry = useCallback(
    async ({ originalError, updates, saveData, saveContext, shouldPromoteStatus }: ResolveConflictAndRetryOptions): Promise<void> => {
      if (!orderId) return;

      try {
        const latestOrder = await refetchOrder({
          reason: 'saveConcurrencyConflict',
          shouldApply: () => false
        });

        if (!latestOrder || !lastPersistedSnapshot) {
          throw originalError;
        }

        const mergeResult = mergeOrderItemData(lastPersistedSnapshot, latestOrder.orderItem.data, saveData);

        if (mergeResult.conflicts.length > 0) {
          applyServerSnapshot(latestOrder.orderItem.data, latestOrder.etag);
          setMergeConflicts(mergeResult.conflicts);
          trackCustomAction('OrderDraftMergeConflict', {
            orderId,
            conflictCount: mergeResult.conflicts.length,
            conflictFields: formatConflictSummary(mergeResult.conflicts)
          });
          throw new OrderItemDataMergeConflictError(mergeResult.conflicts);
        }

        const retryResult = await orderService.updateOrderItem(
          orderId,
          {
            ...updates,
            data: JSON.stringify(mergeResult.data)
          },
          latestOrder.etag ?? etagRef.current,
          editLeaseIdRef.current
        );
        throwIfEditLeaseConflict(retryResult.lockOwnerName, 'resolveConflictAndRetry');

        if (retryResult.etag) {
          etagRef.current = retryResult.etag;
        }

        if (shouldPromoteStatus) {
          shouldSetStatusToInProgress.current = false;
          updateStatus('In progress');
        }

        const retryRefetchedOrder = retryResult.etag
          ? undefined
          : await refetchOrder({
              reason: 'missingMergeSaveETag',
              shouldApply: saveContext.isLatest
            });
        const persistedData = retryRefetchedOrder?.orderItem.data ?? mergeResult.data;
        const persistedETag = retryRefetchedOrder?.etag ?? retryResult.etag ?? etagRef.current;

        trackCustomAction('OrderDraftMergeSucceeded', {
          orderId,
          conflictCount: 0,
          retried: true,
          isLatest: saveContext.isLatest()
        });

        if (!saveContext.isLatest()) {
          return;
        }

        applyServerSnapshot(persistedData, persistedETag);
        markSnapshotPersisted(persistedData, persistedETag);
      } catch (mergeError) {
        if (!(mergeError instanceof OrderItemDataMergeConflictError)) {
          trackCustomAction('OrderDraftMergeFailed', {
            orderId,
            status: getErrorStatus(mergeError)
          });
        }

        throw mergeError;
      }
    },
    [
      applyServerSnapshot,
      lastPersistedSnapshot,
      markSnapshotPersisted,
      orderId,
      refetchOrder,
      setMergeConflicts,
      throwIfEditLeaseConflict,
      trackCustomAction,
      updateStatus
    ]
  );

  const saveDraftData = useCallback(
    async (saveData: Data, saveContext: SerializedSaveContext): Promise<void> => {
      if (!orderId || !editLeaseIdRef.current) return;

      if (draftConflicts.length > 0) {
        trackCustomAction('OrderDraftMergeBlocked', {
          orderId,
          conflictCount: draftConflicts.length
        });
        throw new OrderItemDataMergeConflictError(draftConflicts);
      }

      const hasDataChanged = !lastPersistedSnapshot || !areDataEqual(lastPersistedSnapshot, saveData);

      if (!hasDataChanged) {
        return;
      }

      const updates: DraftSaveUpdates = {
        data: JSON.stringify(saveData)
      };
      const shouldPromoteStatus = shouldSetStatusToInProgress.current;

      if (shouldPromoteStatus) {
        updates.status = 'In progress';
      }

      try {
        const saveETag = etagRef.current;
        const updateResult = await orderService.updateOrderItem(orderId, updates, saveETag, editLeaseIdRef.current);
        throwIfEditLeaseConflict(updateResult.lockOwnerName, 'saveDraftData');

        if (updateResult.etag) {
          etagRef.current = updateResult.etag;
        }

        if (shouldPromoteStatus) {
          shouldSetStatusToInProgress.current = false;
          updateStatus('In progress');
        }

        const refetchedOrder = updateResult.etag
          ? undefined
          : await refetchOrder({
              reason: 'missingSaveETag',
              shouldApply: saveContext.isLatest
            });

        if (!saveContext.isLatest()) {
          return;
        }

        markSnapshotPersisted(refetchedOrder?.orderItem.data ?? saveData, refetchedOrder?.etag ?? updateResult.etag ?? etagRef.current);
      } catch (error) {
        if (!isConflictError(error)) {
          console.error('Save failed:', error);
          trackError(error as Error, {
            operation: 'saveDraftData'
          });
          throw error;
        }

        trackCustomAction('ConcurrencyConflictDetected', {
          orderId,
          operation: 'saveDraftData'
        });

        return resolveConflictAndRetry({
          originalError: error,
          updates,
          saveData,
          saveContext,
          shouldPromoteStatus
        });
      }
    },
    [
      draftConflicts,
      lastPersistedSnapshot,
      markSnapshotPersisted,
      orderId,
      refetchOrder,
      resolveConflictAndRetry,
      throwIfEditLeaseConflict,
      updateStatus,
      trackCustomAction,
      trackError
    ]
  );

  const {
    status: saveStatus,
    enqueueSave,
    markDirty
  } = useSerializedSaveQueue<Data>({
    save: saveDraftData,
    areEqual: areDataEqual,
    isConflictError
  });

  const onAddressValueChange = (newAddress: string) => {
    if (isEditDisabled) return;

    markDirty();
    setAddressValue(newAddress);
    setOrder((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          Address: { ...prev.data.Address, FormattedAddress: newAddress }
        }
      };
    });

    trackCustomAction('AddressUpdated', {
      orderId
    });
  };

  const handleAnswerValueChange = useCallback(
    (questionId: string, value: string) => {
      if (isEditDisabled) return;

      markDirty();
      setAnswerValue(questionId, value);
    },
    [isEditDisabled, markDirty, setAnswerValue]
  );

  const handleTokenValueChange = useCallback(
    (questionId: string, token: string, value: any) => {
      if (isEditDisabled) return;

      markDirty();
      setTokenValue(questionId, token, value);
    },
    [isEditDisabled, markDirty, setTokenValue]
  );

  const handleRestorePendingDraft = useCallback(() => {
    if (isEditDisabled) return;

    markDirty();
    restorePendingDraft();
  }, [isEditDisabled, markDirty, restorePendingDraft]);

  const getFormattedAnswers = () => {
    return questionsRef.current?.getFormattedAnswers();
  };

  // This handles preventing status changes when invalid tokens are found during validation
  const handleInvalidTokensFound = useCallback((hasInvalidTokens: boolean) => {
    if (hasInvalidTokens) {
      shouldSetStatusToInProgress.current = false;
    }
  }, []);

  const handleQuestionsLoadError = useCallback((hasError: boolean) => {
    setQuestionsLoadError(hasError);
  }, []);

  const triggerSave = useCallback(async (): Promise<void> => {
    if (!order || questionsLoadError || !editLeaseIdRef.current) {
      return;
    }

    const formattedAnswers = getFormattedAnswers();

    if (!formattedAnswers) {
      return;
    }

    const draftData = draftSnapshot;
    if (!draftData) {
      return;
    }

    const updatedData: Data = {
      ...draftData,
      Answers: formattedAnswers
    };
    await enqueueSave(updatedData);
  }, [draftSnapshot, enqueueSave, order, questionsLoadError]);

  const selectForQc = () => {
    if (order?.status === 'QC' || order?.status === 'QC fail') {
      return false;
    }

    const qcRates = {
      [ProductType.Residential]: parseFloat(import.meta.env.VITE_APP_QC_RATE_RESIDENTIAL),
      [ProductType.Commercial]: parseFloat(import.meta.env.VITE_APP_QC_RATE_COMMERCIAL)
    };

    const qcRate = qcRates[order?.productType as ProductType] || 1.0;
    const selectedForQc = qcRate >= 1.0 || Math.random() < qcRate;

    if (selectedForQc) {
      trackCustomAction('OrderSelectedForQC', {
        orderId,
        productType: order?.productType,
        qcRate
      });
    }

    return selectedForQc;
  };

  const openCustomerUploadedPlans = () => {
    customerUploadedPlans.forEach((attachment) => window.open(attachment.toString(), '_blank'));

    trackFeature('ViewCustomerPlans', {
      orderId,
      planCount: customerUploadedPlans.length,
      attachments: customerUploadedPlans.map((attachment) => attachment.toString())
    });
  };

  const handleRegenerateReport = useCallback(async (): Promise<void> => {
    if (!orderId || reportRegenerationStatus === 'loading' || reportRegenerationStatus === 'requested') {
      return;
    }

    setReportRegenerationStatus('loading');

    try {
      await orderService.regenerateReport(orderId);
      setReportRegenerationStatus('requested');

      trackCustomAction('ReportRegenerationRequested', {
        orderId
      });
    } catch (error) {
      setReportRegenerationStatus('failed');
      trackError(error as Error, {
        operation: 'regenerateReport',
        orderId
      });
    }
  }, [orderId, reportRegenerationStatus, trackCustomAction, trackError]);

  const handleReplaceCustomerUploadedPlans = useCallback(
    async (file: File): Promise<void> => {
      if (!orderId || !canReplaceCustomerUploadedPlans || customerUploadedPlansReplacementStatus === 'uploading') {
        return;
      }

      if (!isValidFileType(file)) {
        setCustomerUploadedPlansReplacementStatus('failed');
        trackError(new Error('Invalid customer uploaded plans file type'), {
          operation: 'replaceCustomerUploadedPlans',
          orderId,
          fileName: file.name,
          contentType: file.type
        });
        return;
      }

      setCustomerUploadedPlansReplacementStatus('uploading');
      startMeasure('CustomerUploadedPlansReplacement');

      try {
        const attachmentBytes = await fileToBase64(file);
        const replacement = await orderService.replaceCustomerUploadedPlans(orderId, file.name, attachmentBytes, file.type);

        setCustomerUploadedPlans(replacement.attachmentUrls);
        setCanReplaceCustomerUploadedPlans(replacement.canReplace);
        setCustomerUploadedPlansReplacementStatus('idle');

        trackFileUpload(file.name, file.size, file.type, {
          orderId,
          operation: 'replaceCustomerUploadedPlans'
        });
        trackCustomAction('CustomerUploadedPlansReplaced', {
          orderId,
          attachmentCount: replacement.attachmentUrls.length
        });
        endMeasure('CustomerUploadedPlansReplacement', {
          orderId,
          success: true
        });

        try {
          await refetchOrder({ reason: 'customerUploadedPlansReplacement' });
        } catch {
          // refetchOrder already tracks the failure; the replacement itself has succeeded.
        }
      } catch (error) {
        setCustomerUploadedPlansReplacementStatus('failed');
        trackError(error as Error, {
          operation: 'replaceCustomerUploadedPlans',
          orderId,
          fileName: file.name
        });
        endMeasure('CustomerUploadedPlansReplacement', {
          orderId,
          success: false
        });
      }
    },
    [
      canReplaceCustomerUploadedPlans,
      customerUploadedPlansReplacementStatus,
      endMeasure,
      orderId,
      refetchOrder,
      startMeasure,
      trackCustomAction,
      trackError,
      trackFileUpload
    ]
  );

  const handleBack = useCallback(async () => {
    // Ensure that we make a save attempt on back nav as
    // lost focus my not have triggered in all situations
    try {
      await triggerSave();
    } catch {
      return;
    }

    trackClick('BackButton', 'button', {
      returnPath: navState?.returnPath || '/'
    });

    navigate(navState?.returnPath || '/', {
      state: { activeTab: navState?.fromTab }
    });
  }, [navigate, navState?.fromTab, navState?.returnPath, trackClick, triggerSave]);

  const handleOrdersBreadcrumbClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      void handleBack();
    },
    [handleBack]
  );

  const handleFilesUploaded = async (): Promise<void> => {
    setUploadedFiles([]);
    questionsRef.current?.clearFiles();

    trackCustomAction('BulkFilesUploaded', {
      orderId,
      fileCount: uploadedFiles.length
    });
  };

  const handlePlacedOnHoldCompleted = useCallback(
    async (commentText: string): Promise<void> => {
      if (!editLeaseIdRef.current) return;

      const onHoldStatusId = statusChips['On hold'].id;

      shouldSetStatusToInProgress.current = false;
      try {
        await triggerSave();
      } catch {
        return;
      }
      let commentId: string | undefined;

      try {
        commentId = await orderService.postStatusWithComment(
          orderId!,
          user?.userId!,
          onHoldStatusId,
          commentText,
          editLeaseIdRef.current
        );
      } catch (error) {
        if (handleEditLeaseConflict(getLockOwnerName(error), 'handlePlacedOnHoldCompleted')) {
          return;
        }

        throw error;
      }

      if (commentId) {
        updateStatus('On hold', commentText);
      }

      trackCustomAction('OrderPlacedOnHold', {
        orderId,
        reason: commentText.length > 0 ? 'withComment' : 'withoutComment'
      });
    },
    [handleEditLeaseConflict, orderId, triggerSave, updateStatus, user?.userId, trackCustomAction]
  );

  const isOrderEditable = () => {
    if (!order) return false;
    return order.status !== STATUS_CANCELLED && order.status !== STATUS_COMPLETE;
  };

  if (isLoading || !order) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading order details...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ px: 0, mb: 8 }}>
        <MainCard sx={{ boxShadow: 'none', '& .MuiCardContent-root:last-child': { pb: 1, pt: '10px', px: 2 }, mb: 1 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              color="inherit"
              href={navState?.returnPath || '/'}
              underline="hover"
              variant="body2"
              onClick={handleOrdersBreadcrumbClick}
            >
              Orders
            </Link>
            <Typography color="text.secondary" variant="body2">
              Questions
            </Typography>
          </Breadcrumbs>
        </MainCard>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 0 }}>
          <IconButton onClick={handleBack} aria-label="Back to orders" sx={{ mr: 1, '&:hover': { bgcolor: 'transparent' } }}>
            <FontAwesomeIcon icon={faArrowLeftLong} style={{ fontSize: '1.2rem', transform: 'scaleX(1.2)', color: 'black' }} />
          </IconButton>
          <Typography variant="h4" component="h4" sx={{ fontWeight: 500 }}>
            Questions
          </Typography>
        </Box>

        <EditOrderContext.Provider value={{ orderId: order.id, userId: user?.userId!, isEditable: isOrderEditable() && !isEditDisabled }}>
          <Grid container spacing={3} sx={{ px: 0 }}>
            <Grid item xs={12} md={8}>
              <MainCard>
                {pendingDraft && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button color="inherit" size="small" onClick={handleRestorePendingDraft} disabled={isEditDisabled}>
                          Restore
                        </Button>
                        <Button color="inherit" size="small" onClick={discardPendingDraft}>
                          Discard
                        </Button>
                      </Stack>
                    }
                  >
                    Unsaved draft from {formatDraftTimestamp(pendingDraft.timestamp)} is available for this order.
                  </Alert>
                )}
                {draftConflicts.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Review the conflicting field{draftConflicts.length === 1 ? '' : 's'}: {formatConflictSummary(draftConflicts)}. Keep or
                    adjust your draft, then save again.
                  </Alert>
                )}
                <Questions
                  ref={questionsRef}
                  answerValues={draftAnswers}
                  tokenValues={draftTokens}
                  onAnswerValueChange={handleAnswerValueChange}
                  onTokenValueChange={handleTokenValueChange}
                  onFormValidityChange={setIsFormValid}
                  isReadOnly={isEditDisabled}
                  productCode={order.productType}
                  onFilesChange={setUploadedFiles}
                  orderItemPlans={draftAttachments.map((url) => new URL(url))}
                  onAutoSave={triggerSave}
                  onFileDelete={handleFileDelete}
                  onFileUpload={handleSingleFileUpload}
                  onPlacedOnHoldCompleted={handlePlacedOnHoldCompleted}
                  onInvalidTokensFound={handleInvalidTokensFound}
                  onLoadError={handleQuestionsLoadError}
                  onQuestionSetVersionChange={setQuestionSetVersion}
                />
              </MainCard>
            </Grid>

            <Grid item xs={12} md={4} sx={{ position: 'sticky', top: 50, alignSelf: 'flex-start' }}>
              <MainCard title="Order Details" sx={{ mb: 3 }}>
                <Stack spacing={2}>
                  <Grid container>
                    <Grid item xs={8}>
                      <Typography variant="subtitle2">Order Reference</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {order.orderReference}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2">Status</Typography>
                        <Chip label={status!.label} color={status!.color} size="small" variant="light" sx={{ mt: 0.5 }} />
                      </Box>
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography variant="subtitle2">Type</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                      {order.productType}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Address</Typography>
                    <TextField
                      value={draftAddress.FormattedAddress}
                      multiline
                      minRows={5}
                      onChange={(e) => onAddressValueChange(e.target.value)}
                      required
                      size="small"
                      placeholder="Address"
                      disabled={isEditDisabled}
                      sx={{ width: '250px', bgcolor: 'grey.50' }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Attachments
                    </Typography>
                    <OrderAttachments
                      customerUploadedPlans={customerUploadedPlans}
                      canReplaceCustomerUploadedPlans={canReplaceCustomerUploadedPlans}
                      replacementStatus={customerUploadedPlansReplacementStatus}
                      onOpenCustomerUploadedPlans={openCustomerUploadedPlans}
                      onReplaceCustomerUploadedPlans={handleReplaceCustomerUploadedPlans}
                    />
                  </Box>

                  {order.canRegenerateReport && (
                    <Box>
                      <LoadingButton
                        variant="outlined"
                        color="primary"
                        loading={reportRegenerationStatus === 'loading'}
                        disabled={reportRegenerationStatus === 'loading' || reportRegenerationStatus === 'requested'}
                        onClick={handleRegenerateReport}
                        sx={{ width: '250px' }}
                      >
                        {reportRegenerationStatus === 'requested' ? 'Report regeneration requested' : 'Regenerate Report'}
                      </LoadingButton>
                      {reportRegenerationStatus === 'failed' && (
                        <Alert severity="error" sx={{ mt: 1, width: '250px' }}>
                          Report regeneration could not be requested.
                        </Alert>
                      )}
                    </Box>
                  )}
                </Stack>
              </MainCard>

              <UpdateOrderItemStatus
                orderId={order.id}
                isFormValid={isFormValid}
                onStatusUpdate={updateStatus}
                isHistory={isHistory}
                isQc={order.status === 'QC'}
                selectForQc={selectForQc}
                uploadedFiles={uploadedFiles}
                onFilesUploaded={handleFilesUploaded}
                onSave={triggerSave}
                saveStatus={saveStatus}
                editLeaseId={editLeaseId}
                onLockConflict={handleStatusEditLeaseConflict}
                onReleaseEditLease={releaseEditLease}
              />

              <Comments
                comments={comments!}
                orderId={order.id}
                onAddComment={addComment}
                editLeaseId={editLeaseId}
                onLockConflict={handleCommentEditLeaseConflict}
              />
            </Grid>
          </Grid>
        </EditOrderContext.Provider>
      </Box>

      <LockedOrder isOpen={lockOwnerName !== null} userName={lockOwnerName!} onClose={handleBack} />
    </>
  );
};

export default QuestionsPage;
